import React, { useEffect, useState } from 'react';
import { useOcoBrackets } from '@/contexts/OcoBracketsContext';
import { Box, TextField, MenuItem, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, useTheme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faEdit } from '@fortawesome/pro-solid-svg-icons';
import AutoOcoBrackets from './autoOcoBrackets';
import { TsModal } from '../modal';
import { useTradingAccount } from '@/contexts/TradingAccountContext';

interface OcoBracketSelectorProps {
  selectedBracketId: number | null;
  onBracketChange: (bracketId: number | null) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  label?: string;
  helperText?: string;
  showSettingsButton?: boolean;
  showEditButton?: boolean;
}

const OcoBracketSelector: React.FC<OcoBracketSelectorProps> = ({ selectedBracketId, onBracketChange, disabled = false, size = 'small', fullWidth = true, label = 'OCO Bracket', helperText, showSettingsButton = true, showEditButton = true }) => {
  const { brackets, loading: bracketsLoading } = useOcoBrackets();
  const { activeTradingAccount } = useTradingAccount();
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const theme = useTheme();
  const ocoBracketsDisabled = disabled || !activeTradingAccount.autoOcoBrackets || activeTradingAccount.isFollower;

  const handleBracketChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    onBracketChange(value ? Number(value) : null);
  };

  const openSettingsModal = () => {
    setSettingsModalOpen(true);
  };

  const closeSettingsModal = () => {
    setSettingsModalOpen(false);
  };

  const openEditModal = () => {
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
  };

  const handleSettingsSaved = () => {
    // The create/edit dialog will close automatically
    // The parent modal stays open so the user can continue managing brackets
    // The context will automatically refresh the brackets data
  };

  const handleEditSaved = () => {
    closeEditModal();
    // The context will automatically refresh the brackets data
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', alignContent: 'center', gap: 1, backgroundColor: 'transparent' }}>
        <TextField
          select
          fullWidth={fullWidth}
          size={size}
          label={ocoBracketsDisabled ? 'Position Bracket' : 'OCO Bracket'}
          sx={{ backgroundColor: 'transparent', flex: 1 }}
          value={ocoBracketsDisabled ? 'disabled' : selectedBracketId ?? '-1'}
          onChange={handleBracketChange}
          disabled={ocoBracketsDisabled || bracketsLoading}
        >
          {ocoBracketsDisabled && (
            <MenuItem value='disabled'>
              <span style={{ color: theme.palette.text.secondary }}>Enabled</span>
            </MenuItem>
          )}

          <MenuItem value='-1'>
            <strong>
              <em style={{ color: theme.palette.text.secondary }}>OFF</em>
            </strong>
          </MenuItem>

          {brackets.map((bracket) => (
            <MenuItem key={bracket.id} value={bracket.id}>
              {bracket.name}
            </MenuItem>
          ))}
        </TextField>
        {showSettingsButton && (
          <Box alignSelf='flex-start' sx={{ marginTop: '0.5em' }}>
            <Tooltip title='Manage Brackets' placement='top'>
              <IconButton
                onClick={openSettingsModal}
                disabled={disabled || bracketsLoading}
                size={size}
                sx={{
                  alignSelf: 'flex-end',
                  mb: size === 'small' ? 0.5 : 0.75
                }}
              >
                <FontAwesomeIcon icon={faCog} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
      <TsModal open={settingsModalOpen} onClose={closeSettingsModal} title='Chart Settings'>
        <Box sx={{ maxWidth: '60vw', minWidth: '35vw' }}>
          <AutoOcoBrackets onSaved={handleSettingsSaved} onCancelled={closeSettingsModal} showButtons={true} showCloseButton={true} selectedBracketId={selectedBracketId} onChangeBracket={onBracketChange} />
        </Box>
      </TsModal>
    </>
  );
};

export default OcoBracketSelector;
